import {gql} from '@apollo/client';
import React from 'react';

import {assert} from 'Asserts';
import {setWindowTitle} from 'BenchlingUtil';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import * as RegistryUtil from 'registry/RegistryUtil';
import {
  LocationSchemasSettingsPageWithAddedInfoQuery,
  LocationSchemasSettingsPageWithAddedInfoQueryVariables,
} from 'settings/samples/__generated/LocationSchemasSettingsPageWithAddedInfoQuery';
import sortedBy from 'util/sortedBy';

import LocationSchemasListing, {LocationSchemaListing} from './LocationSchemasListing';

export const LOCATION_SCHEMAS_SETTINGS_PAGE_WITH_ADDED_INFO_QUERY = gql`
  query LocationSchemasSettingsPageWithAddedInfoQuery($registryId: StrictString!)
  @skipAudit
  @engComponent(name: INVENTORY) {
    registry(id: $registryId) {
      ... on RegistryNode {
        id
        locationSchemas {
          id
          name
          fields: fieldsJson
          numValidationFailures
          ...HasArchiveRecordGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('HasArchiveRecord').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface Props {
  ownerUrlId: string;
  registryId: string;
}

interface QueryingProps {
  isWritable: boolean;
  locationSchemas: Array<LocationSchemaListing> | null;
}

function LocationSchemasPage(props: Props & QueryingProps): JSX.Element {
  React.useEffect(() => {
    setWindowTitle('Location Schemas - Registry Settings');
  }, []);

  return (
    <LocationSchemasListing
      isWritable={props.isWritable}
      locationSchemas={props.locationSchemas}
      ownerUrlId={props.ownerUrlId}
      registryId={props.registryId}
    />
  );
}

function QueryingLocationSchemasPage(props: Props): JSX.Element {
  const isWritable = RegistryUtil.useIsRegistryWritableQuery(props.registryId);
  const locationSchemasQuery = useBenchlingQuery<
    LocationSchemasSettingsPageWithAddedInfoQuery,
    LocationSchemasSettingsPageWithAddedInfoQueryVariables
  >(LOCATION_SCHEMAS_SETTINGS_PAGE_WITH_ADDED_INFO_QUERY, {variables: {registryId: props.registryId}});

  const loadedPage = locationSchemasQuery.type === QueryResultType.SUCCESS;
  const registry = loadedPage ? locationSchemasQuery.data.registry : null;
  if (registry) {
    assert(!isInaccessible(registry), 'Expected registry to be accessible');
  }
  const locationSchemas: Array<LocationSchemaListing> | null =
    registry != null
      ? sortedBy(registry.locationSchemas, [
          {
            accessor: ({name}) => name,
            naturalSort: true,
          },
        ])
      : null;

  return <LocationSchemasPage {...props} locationSchemas={locationSchemas} isWritable={isWritable} />;
}

export default QueryingLocationSchemasPage;
